// services/api.js - Очищенный API сервис с реальным бэкендом
import { API_CONSTANTS, API_STATUS } from '../types/api';
import { authStorage } from '../utils/storage';

// =============================================================================
// КОНФИГУРАЦИЯ BACKEND API
// =============================================================================
const BACKEND_CONFIG = {
    BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:7000/api',
    TIMEOUT: 10000,
    HEADERS: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
};

// =============================================================================
// HTTP CLIENT UTILITIES
// =============================================================================
const createApiResponse = (data = null, error = null, message = '') => {
    return {
        status: error ? API_STATUS.ERROR : API_STATUS.SUCCESS,
        data,
        error,
        message,
        timestamp: new Date().toISOString()
    };
};

const createApiError = (type, message, details = {}) => {
    return {
        type,
        message,
        details,
        timestamp: new Date().toISOString()
    };
};

const makeRequest = async (endpoint, options = {}) => {
  const url = `${BACKEND_CONFIG.BASE_URL}${endpoint}`;
  const config = {
    method: 'GET',
    headers: { ...BACKEND_CONFIG.HEADERS },
    ...options
  };

  // Добавляем токен авторизации если есть
  const token = authStorage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Добавляем тело запроса для POST/PUT
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BACKEND_CONFIG.TIMEOUT);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Очищаем токен при 401
        authStorage.clearAuthData();
      }
      throw createApiError(
        response.status === 401 ? 'AUTH_ERROR' :
        response.status === 400 ? 'VALIDATION_ERROR' :
        response.status === 404 ? 'NOT_FOUND' : 'SERVER_ERROR',
        responseData?.error || responseData?.message || `HTTP ${response.status}`,
        { status: response.status, response: responseData }
      );
    }

    // ИСПРАВЛЕНИЕ: Возвращаем прямой ответ от сервера для auth методов
    // Для auth методов сервер сам возвращает нужную структуру
    if (endpoint.startsWith('/auth/')) {
      return responseData; // Прямой ответ от сервера
    }

    // Для остальных методов используем обертку
    return createApiResponse(responseData, null, responseData?.message || 'Success');
  } catch (error) {
    if (error.name === 'AbortError') {
      throw createApiError('NETWORK_ERROR', 'Запрос превысил время ожидания');
    }
    
    if (error.type) {
      // Уже обработанная ошибка API
      throw error;
    }
    
    // Сетевая ошибка
    throw createApiError(
      'NETWORK_ERROR',
      'Ошибка сети. Проверьте подключение к интернету.',
      { originalError: error.message }
    );
  }
};

// =============================================================================
// AUTHENTICATION API
// =============================================================================
export const authApi = {
    login: async (credentials) => {
        console.log('API login called with:', credentials);
        return await makeRequest('/auth/login', {
            method: 'POST',
            body: credentials
        });
    },

    register: async (userData) => {
        console.log('API register called with:', userData);
        return await makeRequest('/auth/register', {
            method: 'POST',
            body: userData
        });
    },

    logout: async () => {
        return await makeRequest('/auth/logout', {
            method: 'POST'
        });
    },

    getProfile: async () => {
        console.log('API getProfile called');
        return await makeRequest('/auth/profile', {
            method: 'GET'
        });
    },

    refreshToken: async (refreshToken) => {
        return await makeRequest('/auth/refresh', {
            method: 'POST',
            body: { refreshToken }
        });
    },

    changePassword: async (passwordData) => {
        return await makeRequest('/auth/change-password', {
            method: 'POST',
            body: passwordData
        });
    },

    forgotPassword: async (email) => {
        return await makeRequest('/auth/forgot-password', {
            method: 'POST',
            body: { email }
        });
    },

    updateProfile: async (userData) => {
        return await makeRequest('/auth/profile', {
            method: 'PUT',
            body: userData
        });
    }
};

// =============================================================================
// CONTRACTS API
// =============================================================================
export const contractsApi = {
    getContract: async (contractId) => {
        return await makeRequest(`/contracts/${contractId}`);
    },

    getContracts: async (page = 1, pageSize = 10) => {
        return await makeRequest(`/contracts?page=${page}&pageSize=${pageSize}`);
    },

    createContract: async (contractData) => {
        return await makeRequest('/contracts', {
            method: 'POST',
            body: contractData
        });
    },

    updateContract: async (contractId, contractData) => {
        return await makeRequest(`/contracts/${contractId}`, {
            method: 'PUT',
            body: contractData
        });
    },

    deleteContract: async (contractId) => {
        return await makeRequest(`/contracts/${contractId}`, {
            method: 'DELETE'
        });
    },

    downloadWord: async (contractId) => {
        const url = `${BACKEND_CONFIG.BASE_URL}/contracts/${contractId}/download/word`;
        const token = localStorage.getItem(API_CONSTANTS.TOKEN_KEY);
        
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error('Ошибка при скачивании документа');
        }
        
        return response.blob();
    },

    downloadPdf: async (contractId) => {
        const url = `${BACKEND_CONFIG.BASE_URL}/contracts/${contractId}/download/pdf`;
        const token = localStorage.getItem(API_CONSTANTS.TOKEN_KEY);
        
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error('Ошибка при скачивании документа');
        }
        
        return response.blob();
    },
};

// =============================================================================
// ОСНОВНОЙ API КЛАСС
// =============================================================================
class ApiService {
    constructor() {
        this.baseUrl = BACKEND_CONFIG.BASE_URL;
        this.timeout = BACKEND_CONFIG.TIMEOUT;
    }

    getToken() {
        return localStorage.getItem(API_CONSTANTS.TOKEN_KEY);
    }

    setToken(token, expiresIn) {
        localStorage.setItem(API_CONSTANTS.TOKEN_KEY, token);
        const expiryTime = Date.now() + (expiresIn * 1000);
        localStorage.setItem(API_CONSTANTS.TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    removeToken() {
        localStorage.removeItem(API_CONSTANTS.TOKEN_KEY);
        localStorage.removeItem(API_CONSTANTS.TOKEN_EXPIRY_KEY);
        localStorage.removeItem(API_CONSTANTS.USER_KEY);
    }

    isTokenValid() {
        const token = this.getToken();
        const expiryTime = localStorage.getItem(API_CONSTANTS.TOKEN_EXPIRY_KEY);
        
        if (!token || !expiryTime) {
            return false;
        }
        
        return Date.now() < parseInt(expiryTime);
    }

    // Методы аутентификации
    login = authApi.login;
    register = authApi.register;
    logout = authApi.logout;
    getProfile = authApi.getProfile;

    // Методы работы с договорами
    getContract = contractsApi.getContract;
    
    saveContract = async (token, contractData) => {
        if (contractData.id) {
            return await contractsApi.updateContract(contractData.id, contractData);
        } else {
            return await contractsApi.createContract(contractData);
        }
    };
    
    deleteContract = contractsApi.deleteContract;
    
    exportContract = async (token, exportData) => {
        const { format, contractId } = exportData;
        if (format === 'word') {
            const blob = await contractsApi.downloadWord(contractId);
            return createApiResponse({
                fileName: `contract_${contractId}.docx`,
                downloadUrl: URL.createObjectURL(blob),
                format,
                generatedAt: new Date().toISOString()
            });
        } else if (format === 'pdf') {
            const blob = await contractsApi.downloadPdf(contractId);
            return createApiResponse({
                fileName: `contract_${contractId}.pdf`,
                downloadUrl: URL.createObjectURL(blob),
                format,
                generatedAt: new Date().toISOString()
            });
        }
    };
}

// Создаем и экспортируем единственный экземпляр
const apiService = new ApiService();
export default apiService;